import { DigitalTwinTranslations } from 'api/digitalTwin/digitalTwin.api'
import React, { ReactElement, useDeferredValue, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { chartStore, hideReturnIdInGroup, showReturnIdInGroup } from 'store/chart/chart'
import { dynamicClassName } from 'styles/helper'
import { Button } from 'ui/atoms'
import NewIcon from 'ui/atoms/NewIcon/NewIcon'
import { MODIFY_SETTING_COLUMN_RETURN_ID } from 'ui/components/DigitalTwinSettingForm/constants'
import Tooltip from 'ui/components/Tooltip/Tooltip'
import { useSnapshot } from 'valtio'

import { getPrettyName } from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'

import styles from './ChartLegend.module.less'
import { useCollapseLegends } from './useCollapseLegends'

type ChartLegendProps = {
  groupId: string
  persistKey?: string
  digitalTwinTranslations?: DigitalTwinTranslations
  settingInfo?: { name: string; attribute: string }
}

export default function ChartLegend({
  groupId,
  persistKey,
  digitalTwinTranslations,
  settingInfo,
}: ChartLegendProps): ReactElement {
  const { t } = useTranslation()
  const { canCollapse, isOpen, toggleOpen } = useCollapseLegends(persistKey)
  const chartSnap = useSnapshot(chartStore)
  const hiddenReturnIds: Set<string> = chartSnap.groupIdHiddenReturnIds[groupId] || new Set()
  const [clickCount, setClickCount] = useState(0)
  const [singleClickTimer, setSingleClickTimer] = useState<NodeJS.Timeout | null>(null)

  const items = useDeferredValue(chartSnap.groupIdItems[groupId] || [])
  // hiddenReturnIds can contain stale ids from items no longer in this group (e.g. shared groupIds
  // across chart instances), so base the toggle state only on ids that belong to the current items.
  const hiddenItemCount = items.filter((item) => hiddenReturnIds.has(item.data_id)).length
  const visibleItems = isOpen ? items : []
  const toggleLabel = isOpen ? t('Hide legend') : t('Show legend')
  const seriesCountLabel = t('Shows {{selected}} of {{total}} series', {
    selected: items.length - hiddenItemCount,
    total: items.length,
  })
  const isCollapsible = canCollapse && items.length > 0

  return (
    <div
      className={dynamicClassName({
        [styles.ChartLegend]: true,
        [styles.ChartLegend__collapsible]: isCollapsible,
        [styles.ChartLegend__collapsed]: isCollapsible && !isOpen,
      })}
    >
      <div className={isCollapsible ? styles.ChartLegend_Header : styles.ChartLegend_Header__inline}>
        {isCollapsible && (
          <div className={isOpen ? styles.ChartLegend_Toggle : styles.ChartLegend_Collapsed}>
            <Tooltip
              arrow
              followCursor={!isOpen}
              display={isOpen ? undefined : 'block'}
              title={toggleLabel}
              placement="top"
            >
              <button
                type="button"
                aria-expanded={isOpen}
                aria-label={toggleLabel}
                className={isOpen ? styles.ChartLegend_Toggle_Button : styles.ChartLegend_Collapsed_Button}
                onClick={toggleOpen}
              >
                <NewIcon ariaLabel={toggleLabel} icon={isOpen ? 'fal fa-chevron-down' : 'fal fa-chevron-right'} />
                <span className={styles.ChartLegend_ToggleText}>{toggleLabel}</span>
                <span className={styles.ChartLegend_SeriesCount}>{seriesCountLabel}</span>
              </button>
            </Tooltip>
          </div>
        )}

        {isOpen && items.length > 0 && (
          <Button
            secondary
            marginTop
            marginRight
            marginBottom
            hideBorder
            onClick={() => {
              if (hiddenItemCount === 0) {
                hideReturnIdInGroup({ groupId, returnId: items.map((i) => i.data_id) })
              } else {
                showReturnIdInGroup({ groupId, returnId: items.map((i) => i.data_id) })
              }
            }}
          >
            {hiddenItemCount === 0 ? t('Hide all series') : t('Show all series')}
          </Button>
        )}
      </div>

      {visibleItems.map((item, index) => {
        const isHidden = hiddenReturnIds.has(item.data_id)
        const style: Record<string, string | undefined> = {}

        const settingsName =
          item.display_title ??
          (item.title === MODIFY_SETTING_COLUMN_RETURN_ID && settingInfo
            ? `${getPrettyName(settingInfo.name, digitalTwinTranslations)}: ${getPrettyName(settingInfo.attribute, digitalTwinTranslations)}`
            : getPrettyName(item.title, digitalTwinTranslations))

        if (item.dashed) {
          style.border = `dashed ${item.color}`
        } else if (!item.fill) {
          style.border = `2px solid ${item.color}`
        } else {
          style.backgroundColor = item.color
        }

        return (
          <div
            key={`${item.data_id}_${index}`}
            className={styles.ChartLegend_Item}
            onClick={() => {
              setClickCount((prevCount) => prevCount + 1)
              if (singleClickTimer) {
                clearTimeout(singleClickTimer)
              }

              setSingleClickTimer(
                setTimeout(() => {
                  if (clickCount === 0) {
                    // Single click
                    if (isHidden) {
                      showReturnIdInGroup({ groupId, returnId: item.data_id })
                    } else {
                      hideReturnIdInGroup({ groupId, returnId: item.data_id })
                    }
                  } else if (clickCount === 1) {
                    // Double click
                    showReturnIdInGroup({ groupId, returnId: item.data_id })
                    hideReturnIdInGroup({
                      groupId,
                      returnId: items.filter((i) => i.data_id !== item.data_id).map((i) => i.data_id),
                    })
                  }

                  setClickCount(0)
                }, 200)
              ) // 200ms delay
            }}
          >
            <span
              className={dynamicClassName({
                [styles.ChartLegend_Item_Marker]: true,
                [styles.ChartLegend_Item_Marker__hidden]: isHidden,
              })}
              style={style}
            />

            <p
              className={dynamicClassName({
                [styles.ChartLegend_Item_Text]: true,
                [styles.ChartLegend_Item_Text__hidden]: isHidden,
              })}
            >
              {settingsName}
            </p>
          </div>
        )
      })}
    </div>
  )
}
